import { Box, Button, HStack, IconButton, Input, InputGroup, Text } from "@chakra-ui/react-v3";
import { FiSearch, FiX } from "react-icons/fi";
import { RiZoomInLine, RiZoomOutLine, RiDragMoveLine } from "react-icons/ri";

type PdfControlsProps = {
  searchText: string;
  filterInputRef: React.RefObject<HTMLInputElement>;
  handleFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClear: () => void;
  totalMatches: number;
  currentMatchIndex: number;
  navigateToNext: () => void;
  navigateToPrevious: () => void;
  scale: number;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleResetZoom: () => void;
  isPanning: boolean;
  isLoading: boolean;
  setIsPanning: (isPanning: boolean) => void;
  numPages: number | null;
  initialSearchQuery?: string;
};

export function PdfControls({
  searchText,
  filterInputRef,
  handleFilterChange,
  handleClear,
  totalMatches,
  currentMatchIndex,
  navigateToNext,
  navigateToPrevious,
  scale,
  handleZoomIn,
  handleZoomOut,
  handleResetZoom,
  isPanning,
  isLoading,
  setIsPanning,
  numPages,
  initialSearchQuery,
}: PdfControlsProps) {
  return (
    <HStack
      justify="space-between"
      p={3}
      bg="bg.subtle"
      borderBottom="1px solid"
      borderColor="border"
      gap={4}
    >
      {/* Search Bar */}
      <HStack gap={2}>
        <InputGroup
          maxW="350px"
          startElement={<FiSearch />}
          endElement={
            <>
              {searchText.length > 0 && (
                <Box position="absolute" right="2" top="50%" transform="translateY(-50%)">
                  <IconButton
                    size="xs"
                    aria-label="Clear search"
                    onClick={handleClear}
                    variant="ghost"
                    color="fg.muted"
                    borderRadius="full"
                    bg="interactive.hover"
                    _hover={{
                      bg: "interactive.active",
                      color: "fg",
                    }}
                  >
                    <FiX />
                  </IconButton>
                </Box>
              )}
            </>
          }
        >
          <Input
            ref={filterInputRef}
            placeholder="Search in document... (Cmd/Ctrl+F)"
            disabled={isLoading}
            onChange={handleFilterChange}
            defaultValue={initialSearchQuery}
            size="sm"
            bg="bg.surface"
            transition="all 0.15s ease-in-out"
            boxShadow="0 0 0 1px transparent"
            borderColor="transparent"
            _hover={{ borderColor: "focusRing" }}
            _focus={{
              borderColor: "focusRing",
              boxShadow: "0 0 0 3px token(colors.focusRing.shadow)",
              outline: "none",
            }}
          />
        </InputGroup>
        {totalMatches > 0 && (
          <>
            <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap" minW="60px">
              {currentMatchIndex + 1} of {totalMatches}
            </Text>
            <Button
              size="xs"
              onClick={navigateToPrevious}
              variant="ghost"
              title="Previous match (Shift+Enter)"
            >
              ↑
            </Button>
            <Button size="xs" onClick={navigateToNext} variant="ghost" title="Next match (Enter)">
              ↓
            </Button>
          </>
        )}
      </HStack>

      {/* Zoom and Pan Controls */}
      <HStack gap={2}>
        <IconButton
          size="sm"
          aria-label="Toggle pan mode"
          onClick={() => setIsPanning(!isPanning)}
          variant={isPanning ? "solid" : "outline"}
          colorScheme={isPanning ? "blue" : "gray"}
          title="Pan mode"
        >
          <RiDragMoveLine />
        </IconButton>
        <IconButton
          size="sm"
          aria-label="Zoom out"
          onClick={handleZoomOut}
          disabled={scale <= 0.5}
          variant="outline"
        >
          <RiZoomOutLine />
        </IconButton>
        <Text fontSize="sm" color="fg" minW="50px" textAlign="center">
          {Math.round(scale * 100)}%
        </Text>
        <IconButton
          size="sm"
          aria-label="Zoom in"
          onClick={handleZoomIn}
          disabled={scale >= 3.0}
          variant="outline"
        >
          <RiZoomInLine />
        </IconButton>
        <Button size="sm" onClick={handleResetZoom} variant="ghost">
          Reset
        </Button>
        {numPages && (
          <Text fontSize="sm" color="fg.muted" ml={2}>
            {numPages} {numPages === 1 ? "page" : "pages"}
          </Text>
        )}
      </HStack>
    </HStack>
  );
}
